import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FileText, Layers, Loader2, X } from 'lucide-react';
import api from '../../services/api';
import DocumentViewer from '../ui/DocumentViewer';

/**
 * SujetDocsButton
 *
 * Bouton uniforme pour afficher les documents source d'un sujet perso.
 * - 0 doc        → ne s'affiche pas
 * - 1 doc        → bouton direct qui ouvre le viewer
 * - N docs (≥2)  → bouton "Voir les N sources" qui ouvre une popover de choix
 *
 * Les docs sont chargés via /api/custom/document.php?id=X (auth JWT).
 * On fetch en blob côté client puis on passe l'URL blob au DocumentViewer
 * (évite d'exposer le token en query param).
 *
 * Props:
 *   documents : Array<{ id, file_path, original_name, mime_type, size_bytes }>
 *   compact   : bool — affichage compact pour les listes denses
 */
export default function SujetDocsButton({ documents = [], compact = false }) {
    const [popoverOpen, setPopoverOpen] = useState(false);
    const [popoverPos, setPopoverPos] = useState(null); // { top, left } position fixed
    const [loadingDocId, setLoadingDocId] = useState(null);
    const [viewerState, setViewerState] = useState(null); // { src, name, mime, blobUrl }
    const containerRef = useRef(null);
    const popoverContentRef = useRef(null);

    const nbDocs = documents.length;
    const buttonSize = compact ? 14 : 16;
    const fontSize = compact ? '0.78em' : '0.85em';

    // Ferme la popover au clic extérieur (sur le bouton ET sur le contenu rendu via portal)
    useEffect(() => {
        if (!popoverOpen) return;
        const handler = (e) => {
            const inButton = containerRef.current && containerRef.current.contains(e.target);
            const inPopover = popoverContentRef.current && popoverContentRef.current.contains(e.target);
            if (!inButton && !inPopover) setPopoverOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [popoverOpen]);

    // Calcule la position fixed de la popover au moment de l'ouverture
    // (position:fixed + portal pour échapper aux stacking contexts et overflow des modales parentes)
    const openPopover = () => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            // Anti-débordement à droite : si pas de place, on aligne à droite du bouton
            const popoverWidth = Math.min(400, window.innerWidth - 32);
            const leftIdeal = rect.left;
            const left = leftIdeal + popoverWidth > window.innerWidth - 16
                ? Math.max(16, rect.right - popoverWidth)
                : leftIdeal;
            setPopoverPos({ top: rect.bottom + 4, left });
        }
        setPopoverOpen(true);
    };

    // Cleanup blob URL au démontage ou changement de doc affiché
    useEffect(() => {
        return () => {
            if (viewerState?.blobUrl) URL.revokeObjectURL(viewerState.blobUrl);
        };
    }, [viewerState]);

    const openDoc = async (doc) => {
        if (!doc.id) {
            // Cas legacy : pas d'id (orphelin migration) — non supporté pour l'instant
            alert('Document non consultable en ligne. Téléchargez-le via l\'interface admin.');
            return;
        }
        setLoadingDocId(doc.id);
        try {
            const response = await api.get(`/custom/document.php?id=${doc.id}`, { responseType: 'blob' });
            const blob = response.data;
            const blobUrl = URL.createObjectURL(blob);
            // Détermine le nom du fichier pour l'affichage
            const ext = (doc.original_name || doc.file_path || '').split('.').pop().toLowerCase();
            // DocumentViewer détecte le type via l'extension — on lui passe l'original_name
            setViewerState({
                src: blobUrl,
                name: doc.original_name,
                ext,
                blobUrl, // gardé pour révocation
            });
            setPopoverOpen(false);
        } catch (e) {
            alert('Erreur de chargement : ' + (e.response?.data?.error || e.message));
        }
        setLoadingDocId(null);
    };

    const closeViewer = () => {
        if (viewerState?.blobUrl) URL.revokeObjectURL(viewerState.blobUrl);
        setViewerState(null);
    };

    if (nbDocs === 0) return null;

    return (
        <div style={{ display: 'inline-block' }} ref={containerRef}>
            {nbDocs === 1 ? (
                <button type="button" className="btn btn-sm btn-outline"
                    onClick={() => openDoc(documents[0])}
                    disabled={loadingDocId === documents[0].id}>
                    {loadingDocId === documents[0].id
                        ? <Loader2 size={buttonSize} className="spin" />
                        : <FileText size={buttonSize} />}
                    {!compact && <span style={{ marginLeft: 4 }}>Voir</span>}
                </button>
            ) : (
                <button type="button" className="btn btn-sm btn-outline"
                    onClick={() => popoverOpen ? setPopoverOpen(false) : openPopover()}
                    title={`${nbDocs} documents sources`}>
                    <Layers size={buttonSize} />
                    <span style={{ marginLeft: 4 }}>{nbDocs} sources</span>
                </button>
            )}

            {/* Popover rendue via portal en position:fixed pour échapper aux stacking contexts
                des modales parentes (sem-modal, quiz-modal, etc.) qui pouvaient la tronquer/cacher */}
            {popoverOpen && popoverPos && nbDocs > 1 && createPortal(
                <div ref={popoverContentRef} style={{
                    position: 'fixed', top: popoverPos.top, left: popoverPos.left, zIndex: 11000,
                    background: 'var(--surface, #fff)', border: '1px solid var(--card-border)',
                    borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                    minWidth: 280, maxWidth: 'min(400px, calc(100vw - 32px))', overflow: 'hidden',
                }}>
                    <div style={{
                        padding: '8px 12px', borderBottom: '1px solid var(--card-border)',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        fontSize: '0.85em', fontWeight: 600,
                    }}>
                        <span><Layers size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> {nbDocs} sources</span>
                        <button onClick={() => setPopoverOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                            <X size={14} />
                        </button>
                    </div>
                    <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                        {documents.map((d, i) => (
                            <button key={d.id || i} type="button"
                                onClick={() => openDoc(d)}
                                disabled={loadingDocId === d.id}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 8,
                                    width: '100%', padding: '8px 12px',
                                    background: 'none', border: 'none', textAlign: 'left',
                                    cursor: 'pointer', fontSize: '0.85em',
                                    borderBottom: i < documents.length - 1 ? '1px solid var(--card-border)' : 'none',
                                    transition: 'background 0.1s',
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-hover, rgba(139,92,246,0.06))'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}>
                                {loadingDocId === d.id
                                    ? <Loader2 size={14} className="spin" />
                                    : <FileText size={14} style={{ color: 'var(--primary, #8B5CF6)' }} />}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {d.original_name || `Document ${i + 1}`}
                                    </div>
                                    {d.size_bytes && (
                                        <div style={{ fontSize: '0.78em', color: 'var(--text-muted)' }}>
                                            {(d.size_bytes / 1024).toFixed(0)} Ko
                                        </div>
                                    )}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>,
                document.body
            )}

            {viewerState && createPortal(
                <DocumentViewer
                    src={viewerState.src}
                    name={viewerState.name}
                    onClose={closeViewer}
                />,
                document.body
            )}
        </div>
    );
}
